/**
 * Les mesures d'accord d'une étude de concordance.
 *
 * Un pourcentage d'accord brut ne veut presque rien dire sur une série où la
 * plupart des bandes sont absentes : deux lecteurs qui diraient « non » au
 * hasard s'accorderaient déjà à 80 %. Le kappa de Cohen retire cet accord dû
 * au hasard, et c'est le premier chiffre qu'un évaluateur ou un relecteur
 * réclame — son absence suffit à faire écarter une analyse.
 *
 * Il vient toujours avec son dénominateur et son intervalle de confiance.
 * Sans eux, un kappa de 1,00 sur trois bandelettes se lit comme un résultat
 * alors que c'est une coïncidence : l'intervalle, lui, dira « de -0,2 à 1,0 »
 * et personne ne s'y trompera.
 */

export interface Agreement {
  /** Nombre d'observations comparées — le dénominateur, jamais implicite. */
  n: number
  /** Accord observé, de 0 à 1. */
  observed: number
  /**
   * Bornes à 95 % de l'accord observé, par la méthode du score (Wilson).
   *
   * La FDA demande les mesures d'accord « and corresponding confidence
   * intervals » : un accord nu laisse croire que 96 % sur 25 bandelettes et
   * 96 % sur 2 500 disent la même chose. L'intervalle est conditionnel aux
   * résultats observés du comparateur — il ignore la variabilité de la
   * lecture visuelle elle-même, qui n'est pas un étalon.
   */
  observedCi: [number, number] | null
  /** Kappa de Cohen, ou null quand il n'est pas défini (voir `undefinedReason`). */
  kappa: number | null
  /** Bornes à 95 %, null quand l'approximation ne tient pas (voir `ciNote`). */
  ci: [number, number] | null
  /** Pourquoi l'intervalle manque, quand le kappa, lui, existe. */
  ciNote: string | null
  /** Pourquoi le kappa manque, à afficher plutôt que de laisser un trou. */
  undefinedReason: string | null
  /** La table 2×2, pour qui veut vérifier. */
  table: { bothYes: number; bothNo: number; onlyA: number; onlyB: number }
}

/**
 * Intervalle de Wilson, et non l'intervalle normal usuel.
 *
 * Sur de petits effectifs — c'est notre cas — l'intervalle normal sort de
 * [0, 1] et donne des bornes impossibles : « 92 % [78 % ; 106 %] ». Wilson
 * reste dans les clous et garde sa couverture jusqu'à n très petit. C'est
 * aussi la méthode « score » que la FDA recommande pour ces proportions,
 * l'intervalle exact de Clopper-Pearson étant l'autre choix admis.
 */
export const wilson = (successes: number, n: number): [number, number] => {
  const z = 1.96
  const p = successes / n
  const denom = 1 + (z * z) / n
  const centre = (p + (z * z) / (2 * n)) / denom
  const half = (z / denom) * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))
  return [Math.max(0, centre - half), Math.min(1, centre + half)]
}

/**
 * Compare deux lectures binaires appariées — une bande vue ou non, une
 * conclusion positive ou non — et rend l'accord avec son kappa.
 *
 * @param pairs - une entrée par observation : ce qu'a dit A, ce qu'a dit B.
 */
export function agreementOf(pairs: Array<[boolean, boolean]>): Agreement {
  const n = pairs.length
  const table = { bothYes: 0, bothNo: 0, onlyA: 0, onlyB: 0 }
  pairs.forEach(([a, b]) => {
    if (a && b) table.bothYes += 1
    else if (!a && !b) table.bothNo += 1
    else if (a) table.onlyA += 1
    else table.onlyB += 1
  })

  if (n === 0) {
    return {
      n, observed: 0, observedCi: null, kappa: null, ci: null, ciNote: null, table,
      undefinedReason: 'aucune observation',
    }
  }

  const concordant = table.bothYes + table.bothNo
  const observed = concordant / n
  const observedCi = wilson(concordant, n)
  // Accord attendu si les deux lecteurs répondaient au hasard, chacun en
  // gardant sa propre fréquence de « oui ».
  const aYes = (table.bothYes + table.onlyA) / n
  const bYes = (table.bothYes + table.onlyB) / n
  const expected = aYes * bYes + (1 - aYes) * (1 - bYes)

  // Les deux lecteurs n'ont employé qu'une seule réponse : l'accord attendu
  // vaut 1, le kappa divise par zéro. Ce n'est pas un accord parfait, c'est
  // une absence de variation — le dire plutôt que d'afficher 1,00 ou un trou.
  if (expected >= 1) {
    return {
      n,
      observed,
      observedCi,
      kappa: null,
      ci: null,
      ciNote: null,
      table,
      undefinedReason:
        table.bothYes === n || table.bothNo === n
          ? 'une seule réponse employée des deux côtés'
          : 'pas de variation exploitable',
    }
  }

  const kappa = (observed - expected) / (1 - expected)

  // Accord sans aucune exception, dans un sens ou dans l'autre : l'erreur type
  // de l'approximation normale tombe à zéro et l'intervalle se réduit à un
  // point. Un kappa de 1,00 sur trois bandelettes paraîtrait alors certain,
  // ce qui est précisément l'illusion que ce module doit empêcher. On dit
  // plutôt que l'intervalle n'est pas estimable ainsi, et le dénominateur
  // reste là pour juger.
  if (observed >= 1 || observed <= 0) {
    return {
      n,
      observed,
      observedCi,
      kappa,
      ci: null,
      ciNote: `intervalle non estimable sans exception observée (n = ${n})`,
      table,
      undefinedReason: null,
    }
  }

  // Erreur type usuelle du kappa (Fleiss) : suffisante ici, où le message est
  // l'ordre de grandeur de l'incertitude, pas sa troisième décimale.
  const se = Math.sqrt((observed * (1 - observed)) / (n * (1 - expected) ** 2))
  const half = 1.96 * se
  return {
    n,
    observed,
    observedCi,
    kappa,
    ci: [Math.max(-1, kappa - half), Math.min(1, kappa + half)],
    ciNote: null,
    table,
    undefinedReason: null,
  }
}

/**
 * Comment nommer un kappa, selon l'échelle de Landis & Koch.
 *
 * Un écart assumé avec cette échelle : sa tranche haute va de 0,81 à 1,00 et
 * s'appelle « presque parfait ». À 1,00 exactement, le mot se retourne contre
 * lui-même — il n'y a aucune exception à trouver, donc rien qui manque à la
 * perfection, et lire « presque » fait douter du calcul plutôt que du
 * dénominateur. On dit donc « parfait », et c'est le `ciNote` qui porte la
 * réserve qui compte vraiment : sans exception observée, l'intervalle n'est
 * pas estimable, et n s'affiche à côté. 1,00 sur trois bandelettes se lit
 * alors pour ce qu'il est.
 *
 * Le test porte sur la valeur, pas sur l'affichage : un kappa de 0,996 reste
 * « presque parfait » bien qu'il s'écrive 1,00 une fois arrondi.
 */
export function kappaLabel(kappa: number | null): string {
  if (kappa === null) return '—'
  if (kappa < 0) return 'désaccord'
  if (kappa < 0.21) return 'très faible'
  if (kappa < 0.41) return 'faible'
  if (kappa < 0.61) return 'modéré'
  if (kappa < 0.81) return 'substantiel'
  if (kappa < 1) return 'presque parfait'
  return 'parfait'
}

/** Une proportion avec son intervalle, ou null faute d'observations. */
export interface Proportion {
  n: number
  value: number
  ci: [number, number]
}

/**
 * Accord positif et accord négatif — PPA/NPA.
 *
 * Arithmétiquement, ce sont la sensibilité et la spécificité calculées contre
 * la lecture visuelle. Le nom diffère parce que l'enjeu diffère : parler de
 * sensibilité supposerait que le biologiste ne se trompe jamais, et c'est
 * précisément une des choses que l'étude cherche à regarder. Les agences et
 * les relecteurs demandent PPA/NPA quand le comparateur n'est pas un étalon-or
 * (CLSI EP12) — même chiffre, affirmation plus juste.
 *
 * @param pairs - [ce qu'a vu le biologiste, ce qu'a conclu IRIS]
 */
export function positiveNegativeAgreement(pairs: Array<[boolean, boolean]>): {
  positive: Proportion | null
  negative: Proportion | null
} {
  const benchPositive = pairs.filter(([a]) => a)
  const benchNegative = pairs.filter(([a]) => !a)
  const agreeOn = (subset: Array<[boolean, boolean]>, expected: boolean) =>
    subset.filter(([, b]) => b === expected).length
  return {
    positive: benchPositive.length
      ? {
          n: benchPositive.length,
          value: agreeOn(benchPositive, true) / benchPositive.length,
          ci: wilson(agreeOn(benchPositive, true), benchPositive.length),
        }
      : null,
    negative: benchNegative.length
      ? {
          n: benchNegative.length,
          value: agreeOn(benchNegative, false) / benchNegative.length,
          ci: wilson(agreeOn(benchNegative, false), benchNegative.length),
        }
      : null,
  }
}
